import { useState } from 'react';
import {
    WorkoutExercise,
    WorkoutMedia,
    summarizeExerciseSets,
    formatSetDetail,
    resolveExerciseThumbnail,
} from '../../../lib/workoutRecord';
import { I_EMPTY, I_VIDEO_PLAY } from '../../../types/images';

type MediaClick = (mediaList: any[], clickedIndex: number) => void;

type Props = {
    exercise: WorkoutExercise;
    /** 카드 표시 순번 (1-based). 없으면 exercise.order 사용 */
    displayNumber?: number;
    onMediaClick?: MediaClick;
};

/** 덤벨 아이콘 (썸네일 없을 때 fallback) */
const DumbbellIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6.5 6.5l11 11" />
        <path d="M21 21l-1-1" />
        <path d="M3 3l1 1" />
        <path d="M18 22l4-4" />
        <path d="M2 6l4-4" />
        <path d="M3 10l7-7" />
        <path d="M14 21l7-7" />
    </svg>
);

const WorkoutExerciseCard = ({ exercise, displayNumber, onMediaClick }: Props) => {
    const [isSetOpen, setIsSetOpen] = useState(false);

    const number = displayNumber ?? exercise.order;
    const thumbnailUrl = resolveExerciseThumbnail(exercise);
    const setSummary = summarizeExerciseSets(exercise.sets);
    const media = exercise.mediaContents ?? [];
    const metaChips = [...(exercise.bodyParts ?? []), ...(exercise.equipment ?? [])].filter(Boolean);

    // 이미지 뷰어(ModalImagesViewer)가 기대하는 형태로 매핑 + 합성 _id
    const toViewerItem = (m: WorkoutMedia, i: number) => ({
        _id: `wr-${exercise.order}-${i}`,
        contentType: m.contentType,
        content: m.content,
        thumbnail: m.thumbnail,
    });

    const handleMediaClick = (clickedIndex: number) => {
        if (!onMediaClick || media.length === 0) return;
        onMediaClick(media.map(toViewerItem), clickedIndex);
    };

    return (
        <div className="rounded-[12px] border border-solid border-[#E2E8F0] bg-white p-[14px]">
            {/* 헤더: 순번 + 썸네일 + 이름/요약 */}
            <div className="flex items-start gap-[12px]">
                <div className="flex-shrink-0 w-[20px] h-[20px] rounded-full bg-[#EFF3FB] flex items-center justify-center mt-[2px]">
                    <span className="text-[11px] font-bold text-[#3B82F6]">{number}</span>
                </div>

                <div className="flex-shrink-0 w-[44px] h-[44px] rounded-[8px] bg-[#F1F5F9] border border-solid border-[#E2E8F0] overflow-hidden flex items-center justify-center">
                    {thumbnailUrl ? (
                        <img
                            src={thumbnailUrl}
                            className="w-full h-full object-cover"
                            onError={(e: any) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = I_EMPTY;
                            }}
                            alt=""
                        />
                    ) : (
                        <DumbbellIcon />
                    )}
                </div>

                <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-[6px] flex-wrap">
                        <span className="text-[14px] font-semibold text-[#1F2937]">
                            {exercise.exerciseName}
                        </span>
                        {exercise.isCustomExercise && (
                            <span className="text-[10px] text-[#94A3B8] border border-solid border-[#E2E8F0] rounded-[4px] px-[4px] py-[1px]">
                                직접추가
                            </span>
                        )}
                    </div>
                    {setSummary && (
                        <span className="text-[13px] font-medium text-[#3B82F6] mt-[2px]">
                            {setSummary}
                        </span>
                    )}
                    {metaChips.length > 0 && (
                        <span className="text-[12px] text-[#94A3B8] mt-[2px] truncate">
                            {metaChips.join(' · ')}
                        </span>
                    )}
                </div>
            </div>

            {/* 코칭큐 (회원이 보는 문구) */}
            {exercise.cue && (
                <div className="mt-[10px] rounded-[8px] bg-[#EFF6FF] border-l-[3px] border-solid border-[#3B82F6] px-[12px] py-[8px]">
                    <span className="text-[11px] font-semibold text-[#2563EB]">코칭큐</span>
                    <p className="text-[13px] text-[#1E3A8A] leading-[19px] mt-[2px] whitespace-pre-wrap">
                        {exercise.cue}
                    </p>
                </div>
            )}

            {/* 운동 정보 */}
            {exercise.exerciseInfo && (
                <p className="mt-[8px] text-[12px] text-[#6B7280] leading-[18px] whitespace-pre-wrap">
                    {exercise.exerciseInfo}
                </p>
            )}

            {/* 운동별 영상/이미지 */}
            {media.length > 0 && (
                <div className="mt-[10px] flex items-center gap-[8px] flex-wrap">
                    {media.map((m, i) => (
                        <div
                            key={`${exercise.order}-media-${i}`}
                            onClick={() => handleMediaClick(i)}
                            onKeyDown={(event) => {
                                if (!onMediaClick || (event.key !== 'Enter' && event.key !== ' ')) return;
                                event.preventDefault();
                                handleMediaClick(i);
                            }}
                            role={onMediaClick ? 'button' : undefined}
                            tabIndex={onMediaClick ? 0 : undefined}
                            aria-label={
                                onMediaClick
                                    ? `${exercise.exerciseName} ${
                                          m.contentType === 'VIDEO' ? '영상' : '이미지'
                                      } ${i + 1} 열기`
                                    : undefined
                            }
                            className={`relative w-[72px] h-[72px] rounded-[8px] overflow-hidden bg-[#0F172A] group ${
                                onMediaClick ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#3B82F6]' : ''
                            }`}
                        >
                            <img
                                src={m.thumbnail || m.content}
                                className="w-full h-full object-cover opacity-90 group-hover:opacity-75 transition-opacity"
                                onError={(e: any) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = I_EMPTY;
                                }}
                                alt=""
                            />
                            {m.contentType === 'VIDEO' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <img src={I_VIDEO_PLAY} className="w-[24px] h-[24px]" alt="" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* 세트 상세 (기본 접힘) */}
            {exercise.sets.length > 0 && (
                <div className="mt-[10px]">
                    <button
                        onClick={() => setIsSetOpen((prev) => !prev)}
                        className="flex items-center gap-[4px] text-[12px] font-medium text-[#64748B] hover:text-[#3B82F6] transition-colors cursor-pointer"
                    >
                        <span>세트 상세 {exercise.sets.length}개</span>
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                                transform: isSetOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                            }}
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </button>

                    {isSetOpen && (
                        <div className="mt-[8px] flex flex-col gap-[4px] pl-[2px]">
                            {exercise.sets.map((set, i) => (
                                <span
                                    key={`${exercise.order}-set-${i}`}
                                    className="text-[13px] text-[#475569] leading-[20px]"
                                >
                                    {formatSetDetail(set, i)}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default WorkoutExerciseCard;
